/* ------------------------------------------------------------------ */
/* A1 addressing                                                       */
/* ------------------------------------------------------------------ */
/** 0 -> "A", 25 -> "Z", 26 -> "AA". */
export function columnName(index) {
    let name = '';
    let n = index;
    while (n >= 0) {
        name = String.fromCharCode(65 + (n % 26)) + name;
        n = Math.floor(n / 26) - 1;
    }
    return name;
}
/** "AA" -> 26. Returns -1 for anything that is not a column. */
export function columnIndex(name) {
    if (!/^[A-Za-z]+$/.test(name))
        return -1;
    let index = 0;
    for (const char of name.toUpperCase()) {
        index = index * 26 + (char.charCodeAt(0) - 64);
    }
    return index - 1;
}
export function cellName(r, c) {
    return `${columnName(c)}${r + 1}`;
}
/** Parses "B7" or "$B$7" into coordinates. */
export function parseRef(ref) {
    const match = /^\$?([A-Za-z]+)\$?(\d+)$/.exec(ref.trim());
    if (!match)
        return null;
    const c = columnIndex(match[1]);
    const r = Number(match[2]) - 1;
    if (c < 0 || r < 0 || !Number.isFinite(r))
        return null;
    return { r, c };
}
export const isFormula = (input) => input.trimStart().startsWith('=');
/* ------------------------------------------------------------------ */
/* Value coercion                                                      */
/* ------------------------------------------------------------------ */
/**
 * Whether a typed string should be stored as a number.
 *
 * Leading zeros are kept as text on purpose: an order code like `00713` and a
 * phone number are not quantities, and turning them into 713 loses data the
 * person can never get back by retyping the cell.
 */
export function parseNumber(input) {
    const text = input.trim();
    if (!text)
        return null;
    if (/^0\d+$/.test(text))
        return null;
    const cleaned = text.replace(/,/g, '');
    if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?%?$/i.test(cleaned))
        return null;
    const percent = cleaned.endsWith('%');
    const n = Number(percent ? cleaned.slice(0, -1) : cleaned);
    if (!Number.isFinite(n))
        return null;
    return percent ? n / 100 : n;
}
function toNumber(value) {
    if (value === null || value === undefined || value === '')
        return 0;
    if (typeof value === 'number')
        return value;
    const parsed = parseNumber(value);
    if (parsed === null)
        throw new FormulaError('#VALUE!');
    return parsed;
}
function toText(value) {
    if (value === null || value === undefined)
        return '';
    return String(value);
}
function truthy(value) {
    if (typeof value === 'number')
        return value !== 0;
    const text = toText(value).trim().toUpperCase();
    if (text === 'TRUE')
        return true;
    if (text === 'FALSE' || text === '')
        return false;
    return true;
}
class FormulaError extends Error {
}
const OPERATORS = ['<>', '<=', '>=', '+', '-', '*', '/', '^', '&', '=', '<', '>'];
function tokenize(source) {
    const tokens = [];
    let i = 0;
    while (i < source.length) {
        const char = source[i];
        if (/\s/.test(char)) {
            i += 1;
            continue;
        }
        if (char === '"') {
            let text = '';
            i += 1;
            while (i < source.length) {
                if (source[i] === '"') {
                    // "" inside a quoted string is a literal quote, as in Excel.
                    if (source[i + 1] === '"') {
                        text += '"';
                        i += 2;
                        continue;
                    }
                    break;
                }
                text += source[i];
                i += 1;
            }
            if (source[i] !== '"')
                throw new FormulaError('#PARSE!');
            i += 1;
            tokens.push({ kind: 'string', value: text });
            continue;
        }
        if (/\d/.test(char) || (char === '.' && /\d/.test(source[i + 1] ?? ''))) {
            let text = '';
            while (i < source.length && /[\d.]/.test(source[i])) {
                text += source[i];
                i += 1;
            }
            // Scientific notation: only when an exponent actually follows.
            if (/^[eE]$/.test(source[i] ?? '') && /^[-+\d]$/.test(source[i + 1] ?? '')) {
                text += source[i];
                i += 1;
                if (/[-+]/.test(source[i])) {
                    text += source[i];
                    i += 1;
                }
                while (i < source.length && /\d/.test(source[i])) {
                    text += source[i];
                    i += 1;
                }
            }
            const n = Number(text);
            if (!Number.isFinite(n))
                throw new FormulaError('#NUM!');
            tokens.push({ kind: 'number', value: n });
            continue;
        }
        if (/[A-Za-z_$]/.test(char)) {
            let text = '';
            while (i < source.length && /[A-Za-z0-9_.$]/.test(source[i])) {
                text += source[i];
                i += 1;
            }
            // `SUM(` is a function; `A1` on its own is a reference.
            tokens.push(parseRef(text) ? { kind: 'ref', value: text } : { kind: 'name', value: text });
            continue;
        }
        const op = OPERATORS.find((candidate) => source.startsWith(candidate, i));
        if (op) {
            tokens.push({ kind: 'op', value: op });
            i += op.length;
            continue;
        }
        if ('(),:;%'.includes(char)) {
            tokens.push({ kind: 'punc', value: char });
            i += 1;
            continue;
        }
        throw new FormulaError('#PARSE!');
    }
    return tokens;
}
const flatten = (operand) => Array.isArray(operand) ? operand : [operand];
const single = (operand) => Array.isArray(operand) ? (operand[0] ?? null) : operand;
const BINARY_PRECEDENCE = {
    '=': 1,
    '<>': 1,
    '<': 1,
    '>': 1,
    '<=': 1,
    '>=': 1,
    '&': 2,
    '+': 3,
    '-': 3,
    '*': 4,
    '/': 4,
    '^': 5,
};
class Parser {
    tokens;
    lookup;
    pos = 0;
    constructor(tokens, lookup) {
        this.tokens = tokens;
        this.lookup = lookup;
    }
    peek() {
        return this.tokens[this.pos];
    }
    eat(value) {
        const token = this.peek();
        if (token && (token.kind === 'punc' || token.kind === 'op') && token.value === value) {
            this.pos += 1;
            return true;
        }
        return false;
    }
    expect(value) {
        if (!this.eat(value))
            throw new FormulaError('#PARSE!');
    }
    parse() {
        const value = this.expression(0);
        if (this.pos < this.tokens.length)
            throw new FormulaError('#PARSE!');
        return value;
    }
    expression(minPrecedence) {
        let left = this.unary();
        for (;;) {
            const token = this.peek();
            if (!token || token.kind !== 'op')
                break;
            const precedence = BINARY_PRECEDENCE[token.value];
            if (precedence === undefined || precedence < minPrecedence)
                break;
            this.pos += 1;
            // `^` binds right-to-left: 2^3^2 is 2^(3^2).
            const right = this.expression(token.value === '^' ? precedence : precedence + 1);
            left = applyBinary(token.value, single(left), single(right));
        }
        return left;
    }
    unary() {
        if (this.eat('-'))
            return -toNumber(single(this.unary()));
        if (this.eat('+'))
            return toNumber(single(this.unary()));
        return this.postfix();
    }
    postfix() {
        let value = this.primary();
        while (this.eat('%'))
            value = toNumber(single(value)) / 100;
        return value;
    }
    primary() {
        const token = this.peek();
        if (!token)
            throw new FormulaError('#PARSE!');
        if (token.kind === 'number') {
            this.pos += 1;
            return token.value;
        }
        if (token.kind === 'string') {
            this.pos += 1;
            return token.value;
        }
        if (token.kind === 'punc' && token.value === '(') {
            this.pos += 1;
            const value = this.expression(0);
            this.expect(')');
            return value;
        }
        if (token.kind === 'ref') {
            this.pos += 1;
            const from = parseRef(token.value);
            // A colon after a reference makes it a range: A1:B9.
            const next = this.peek();
            if (next?.kind === 'punc' && next.value === ':') {
                const after = this.tokens[this.pos + 1];
                if (after?.kind !== 'ref')
                    throw new FormulaError('#PARSE!');
                const to = parseRef(after.value);
                if (!to)
                    throw new FormulaError('#REF!');
                this.pos += 2;
                return this.readRange(from, to);
            }
            return this.lookup(from.r, from.c) ?? null;
        }
        if (token.kind === 'name') {
            this.pos += 1;
            const upper = token.value.toUpperCase();
            if (upper === 'TRUE')
                return 'TRUE';
            if (upper === 'FALSE')
                return 'FALSE';
            if (this.eat('(')) {
                const args = [];
                if (!this.eat(')')) {
                    do {
                        args.push(this.expression(0));
                        // Some locales write SUM(1;2); accept both separators.
                    } while (this.eat(',') || this.eat(';'));
                    this.expect(')');
                }
                return callFunction(upper, args);
            }
            throw new FormulaError('#NAME?');
        }
        throw new FormulaError('#PARSE!');
    }
    readRange(from, to) {
        const r0 = Math.min(from.r, to.r);
        const r1 = Math.max(from.r, to.r);
        const c0 = Math.min(from.c, to.c);
        const c1 = Math.max(from.c, to.c);
        // A runaway range would hang the tab it was typed into.
        if ((r1 - r0 + 1) * (c1 - c0 + 1) > 100000)
            throw new FormulaError('#NUM!');
        const values = [];
        for (let r = r0; r <= r1; r += 1) {
            for (let c = c0; c <= c1; c += 1) {
                values.push(this.lookup(r, c) ?? null);
            }
        }
        return values;
    }
}
function applyBinary(op, left, right) {
    switch (op) {
        case '+':
            return toNumber(left) + toNumber(right);
        case '-':
            return toNumber(left) - toNumber(right);
        case '*':
            return toNumber(left) * toNumber(right);
        case '/': {
            const divisor = toNumber(right);
            if (divisor === 0)
                throw new FormulaError('#DIV/0!');
            return toNumber(left) / divisor;
        }
        case '^':
            return toNumber(left) ** toNumber(right);
        case '&':
            return toText(left) + toText(right);
        default:
            return compare(op, left, right) ? 'TRUE' : 'FALSE';
    }
}
function compare(op, left, right) {
    // Two numbers compare as numbers; anything else compares as text, so that
    // "=A1>B1" on names sorts the way a person reading them expects.
    const bothNumeric = (typeof left === 'number' || left === null) && (typeof right === 'number' || right === null);
    const a = bothNumeric ? toNumber(left) : toText(left).toUpperCase();
    const b = bothNumeric ? toNumber(right) : toText(right).toUpperCase();
    switch (op) {
        case '=':
            return a === b;
        case '<>':
            return a !== b;
        case '<':
            return a < b;
        case '>':
            return a > b;
        case '<=':
            return a <= b;
        default:
            return a >= b;
    }
}
/* ------------------------------------------------------------------ */
/* Functions                                                           */
/* ------------------------------------------------------------------ */
/** Values in a range that count as numbers — blanks and text are skipped. */
function numbersIn(args) {
    const out = [];
    for (const arg of args) {
        for (const value of flatten(arg)) {
            if (value === null || value === '')
                continue;
            if (typeof value === 'number') {
                out.push(value);
                continue;
            }
            const parsed = parseNumber(value);
            if (parsed !== null)
                out.push(parsed);
        }
    }
    return out;
}
function round(value, digits) {
    const factor = 10 ** digits;
    // Nudge before rounding so 1.005 at 2dp gives 1.01 rather than 1.00, which
    // is what binary floating point would otherwise produce.
    return Math.round((value + Number.EPSILON) * factor) / factor;
}
/** `COUNTIF`/`SUMIF` criteria: a bare value, or an operator and a value. */
function matches(value, criteria) {
    const text = toText(criteria).trim();
    const opMatch = /^(<>|<=|>=|<|>|=)(.*)$/.exec(text);
    if (opMatch) {
        const operand = parseNumber(opMatch[2]) ?? opMatch[2];
        return compare(opMatch[1], value, operand);
    }
    const target = parseNumber(text);
    if (target !== null)
        return toNumber(value) === target;
    return toText(value).toUpperCase() === text.toUpperCase();
}
function callFunction(name, args) {
    switch (name) {
        case 'SUM':
            return numbersIn(args).reduce((total, n) => total + n, 0);
        case 'PRODUCT':
            return numbersIn(args).reduce((total, n) => total * n, 1);
        case 'AVERAGE': {
            const values = numbersIn(args);
            if (!values.length)
                throw new FormulaError('#DIV/0!');
            return values.reduce((total, n) => total + n, 0) / values.length;
        }
        case 'MIN': {
            const values = numbersIn(args);
            return values.length ? Math.min(...values) : 0;
        }
        case 'MAX': {
            const values = numbersIn(args);
            return values.length ? Math.max(...values) : 0;
        }
        case 'COUNT':
            return numbersIn(args).length;
        case 'COUNTA':
            return args.flatMap(flatten).filter((v) => v !== null && v !== '').length;
        case 'COUNTBLANK':
            return args.flatMap(flatten).filter((v) => v === null || v === '').length;
        case 'COUNTIF': {
            if (args.length < 2)
                throw new FormulaError('#N/A');
            const criteria = single(args[1]);
            return flatten(args[0]).filter((value) => matches(value, criteria)).length;
        }
        case 'SUMIF': {
            if (args.length < 2)
                throw new FormulaError('#N/A');
            const range = flatten(args[0]);
            const criteria = single(args[1]);
            // A third argument sums a parallel range instead of the tested one.
            const target = args.length > 2 ? flatten(args[2]) : range;
            let total = 0;
            range.forEach((value, index) => {
                if (matches(value, criteria))
                    total += toNumber(target[index] ?? null);
            });
            return total;
        }
        case 'IF': {
            if (args.length < 2)
                throw new FormulaError('#N/A');
            const branch = truthy(single(args[0])) ? args[1] : args[2];
            return branch === undefined ? 'FALSE' : single(branch);
        }
        case 'IFERROR':
            // Errors surface as thrown values, so a failing first argument never
            // reaches here — it is caught by the wrapper in `evaluate`.
            return single(args[0]);
        case 'AND':
            return args.flatMap(flatten).every(truthy) ? 'TRUE' : 'FALSE';
        case 'OR':
            return args.flatMap(flatten).some(truthy) ? 'TRUE' : 'FALSE';
        case 'NOT':
            return truthy(single(args[0])) ? 'FALSE' : 'TRUE';
        case 'ABS':
            return Math.abs(toNumber(single(args[0])));
        case 'ROUND':
            return round(toNumber(single(args[0])), Math.trunc(toNumber(single(args[1] ?? 0))));
        case 'ROUNDUP': {
            const digits = Math.trunc(toNumber(single(args[1] ?? 0)));
            const factor = 10 ** digits;
            const value = toNumber(single(args[0]));
            return (value < 0 ? -Math.ceil(-value * factor) : Math.ceil(value * factor)) / factor;
        }
        case 'ROUNDDOWN': {
            const digits = Math.trunc(toNumber(single(args[1] ?? 0)));
            const factor = 10 ** digits;
            const value = toNumber(single(args[0]));
            return Math.trunc(value * factor) / factor;
        }
        case 'FLOOR':
            return Math.floor(toNumber(single(args[0])));
        case 'CEILING':
            return Math.ceil(toNumber(single(args[0])));
        case 'INT':
            return Math.trunc(toNumber(single(args[0])));
        case 'SQRT': {
            const value = toNumber(single(args[0]));
            if (value < 0)
                throw new FormulaError('#NUM!');
            return Math.sqrt(value);
        }
        case 'POWER':
            return toNumber(single(args[0])) ** toNumber(single(args[1]));
        case 'MOD': {
            const divisor = toNumber(single(args[1]));
            if (divisor === 0)
                throw new FormulaError('#DIV/0!');
            return toNumber(single(args[0])) % divisor;
        }
        case 'ROUNDPCT':
        case 'PERCENT':
            return toNumber(single(args[0])) / 100;
        case 'CONCAT':
        case 'CONCATENATE':
            return args.flatMap(flatten).map(toText).join('');
        case 'JOIN':
        case 'TEXTJOIN': {
            const separator = toText(single(args[0]));
            return args
                .slice(1)
                .flatMap(flatten)
                .filter((v) => v !== null && v !== '')
                .map(toText)
                .join(separator);
        }
        case 'LEN':
            return toText(single(args[0])).length;
        case 'UPPER':
            return toText(single(args[0])).toUpperCase();
        case 'LOWER':
            return toText(single(args[0])).toLowerCase();
        case 'TRIM':
            return toText(single(args[0])).trim().replace(/\s+/g, ' ');
        case 'LEFT':
            return toText(single(args[0])).slice(0, Math.trunc(toNumber(single(args[1] ?? 1))));
        case 'RIGHT': {
            const count = Math.trunc(toNumber(single(args[1] ?? 1)));
            return count <= 0 ? '' : toText(single(args[0])).slice(-count);
        }
        case 'MID': {
            const start = Math.trunc(toNumber(single(args[1]))) - 1;
            const count = Math.trunc(toNumber(single(args[2])));
            return toText(single(args[0])).slice(Math.max(0, start), Math.max(0, start) + count);
        }
        case 'VALUE': {
            const parsed = parseNumber(toText(single(args[0])));
            if (parsed === null)
                throw new FormulaError('#VALUE!');
            return parsed;
        }
        case 'TEXT':
            return toText(single(args[0]));
        case 'ISBLANK': {
            const value = single(args[0]);
            return value === null || value === '' ? 'TRUE' : 'FALSE';
        }
        case 'ISNUMBER': {
            const value = single(args[0]);
            return typeof value === 'number' || parseNumber(toText(value)) !== null ? 'TRUE' : 'FALSE';
        }
        case 'PI':
            return Math.PI;
        default:
            throw new FormulaError('#NAME?');
    }
}
/* ------------------------------------------------------------------ */
/* Entry point                                                         */
/* ------------------------------------------------------------------ */
/**
 * Evaluates one cell's input against the rest of the grid.
 *
 * A non-formula comes back as a number when it reads like one and as text
 * otherwise. A formula that cannot be resolved comes back with `error` set
 * and the error string as its value, which is what a spreadsheet shows in the
 * cell — an `#N/A` you can see beats a blank you cannot explain.
 */
export function evaluate(input, lookup) {
    if (!isFormula(input)) {
        const parsed = parseNumber(input);
        return { value: parsed === null ? input : parsed };
    }
    try {
        const tokens = tokenize(input.trimStart().slice(1));
        if (!tokens.length)
            return { value: null };
        const value = single(new Parser(tokens, lookup).parse());
        if (typeof value === 'number' && !Number.isFinite(value)) {
            return { value: '#NUM!', error: '#NUM!' };
        }
        return { value };
    }
    catch (cause) {
        const error = cause instanceof FormulaError ? cause.message : '#ERROR!';
        return { value: error, error };
    }
}
/* ------------------------------------------------------------------ */
/* Whole-grid recalculation                                            */
/* ------------------------------------------------------------------ */
const key = (r, c) => `${r}:${c}`;
/** The marker a cell in a dependency loop resolves to. */
const CIRCULAR = '#CIRCULAR!';
/**
 * Recalculates every formula in a tab.
 *
 * Cells are resolved on demand and memoised, so dependencies are followed in
 * whatever order they happen to point rather than needing a topological sort
 * up front. A cell that is asked for while it is still being computed is a
 * cycle, and reports itself as one instead of recursing until the stack goes.
 */
export function recalculate(cells) {
    const byPosition = new Map();
    for (const cell of cells)
        byPosition.set(key(cell.r, cell.c), cell);
    const resolved = new Map();
    const errors = new Map();
    const inProgress = new Set();
    const lookup = (r, c) => {
        const at = key(r, c);
        if (resolved.has(at))
            return resolved.get(at);
        const cell = byPosition.get(at);
        if (!cell)
            return null;
        if (inProgress.has(at)) {
            errors.set(at, CIRCULAR);
            return CIRCULAR;
        }
        inProgress.add(at);
        const result = evaluate(cell.input ?? '', lookup);
        inProgress.delete(at);
        resolved.set(at, result.value);
        if (result.error)
            errors.set(at, result.error);
        return result.value;
    };
    return cells.map((cell) => {
        const at = key(cell.r, cell.c);
        const value = lookup(cell.r, cell.c) ?? null;
        // Only the cell where the cycle was noticed records the error; the rest of
        // the loop merely received the marker as a value. They are all equally
        // broken, so they all get flagged — otherwise one arbitrary cell of a pair
        // turns red and the other looks fine.
        const error = errors.get(at) ??
            (value === CIRCULAR && isFormula(cell.input ?? '') ? CIRCULAR : undefined);
        // `error` is dropped rather than set to undefined so a cell that has been
        // fixed does not keep an empty error key forever.
        const { error: _previous, ...rest } = cell;
        return error ? { ...rest, v: value, error } : { ...rest, v: value };
    });
}
/** How a cell should read on screen, honouring its format. */
export function displayValue(cell) {
    if (!cell)
        return '';
    const value = cell.v ?? null;
    if (value === null)
        return cell.error ?? '';
    if (typeof value === 'number') {
        const decimals = cell.format?.decimals;
        if (decimals !== undefined)
            return value.toFixed(decimals);
        // Long floats are the usual result of dividing money; trim the noise
        // without pretending to a precision the number does not have.
        return Number.isInteger(value) ? String(value) : String(round(value, 10));
    }
    return String(value);
}
